// Foreground timer alarm: a synthesised repeating Web Audio tone + a
// dismissible optical banner (flashing, steady under prefers-reduced-motion).
// Mirrors the scanner's components/beep.js Web-Audio pattern (lazy
// AudioContext, feature-detected, never throws) but keeps its own context so
// a locked/unlocked state can be reported back to the alarm banner (spec
// "Audio blocked degrades to optical only").
//
// design D7: the AudioContext must be created/resumed from within a user
// gesture to satisfy browser autoplay policy. `unlockTimerAudio()` is the
// gesture hook — call it from a click handler on the timers page (creating or
// controlling a timer both count). It is safe to call repeatedly and safe
// when Web Audio is unavailable.

import { t } from '../i18n.js';
import { reducedMotion } from './motion.js';

const BEEP_INTERVAL_MS = 1200;
const BEEP_FREQUENCY_HZ = 880;
const BEEP_DURATION_MS = 220;

// ---------------------------------------------------------------------------
// Chime ids — canonical source of truth (design Decision G). Mirrors the
// bundled files under web/public/chimes/{id}.ogg. timer-create-form.js
// re-exports these rather than redefining them so there is exactly one list to
// keep in sync with the server's known-chime validation and the bundled files.
// ---------------------------------------------------------------------------

export const CHIME_IDS = ['classic', 'bell', 'chime', 'marimba', 'digital', 'gentle'];
export const DEFAULT_CHIME = 'classic';

/**
 * Coerces an arbitrary persisted/served `chime` value to a known id, falling
 * back to `classic` for null/empty/unrecognised input. This is the
 * path-traversal guard: the resolved value is the only thing ever interpolated
 * into the `/chimes/<id>.ogg` Audio src, so an unknown value can never reach
 * the network layer.
 *
 * @param {string|null|undefined} chime
 * @returns {string}
 */
export function resolveChimeId(chime) {
  return CHIME_IDS.includes(chime) ? chime : DEFAULT_CHIME;
}

let sharedCtx = null;
let unlocked = false;
let unlockAttempted = false;

function getAudioContextClass() {
  return (typeof window !== 'undefined' && (window.AudioContext || window.webkitAudioContext)) || null;
}

/**
 * Lazily creates the module-shared AudioContext (never throws). Reused by
 * both the explicit gesture-unlock call and a defensive lazy-create inside
 * `startAlarmTone()` so an alarm still attempts a tone even if the page
 * somehow fired before a gesture unlocked it (the context then simply stays
 * suspended, per autoplay policy, until a gesture resumes it).
 *
 * @returns {object|null}
 */
function getSharedContext() {
  if (sharedCtx) return sharedCtx;
  try {
    const Ctx = getAudioContextClass();
    if (!Ctx) return null;
    sharedCtx = new Ctx();
    return sharedCtx;
  } catch {
    return null;
  }
}

/**
 * Creates (once) and resumes the shared AudioContext. Call this from inside a
 * user-gesture handler (e.g. the first tap on the timers page) so later
 * alarms are not blocked by autoplay policy. Never throws; returns whether the
 * context is already known to be running.
 *
 * @returns {boolean}
 */
export function unlockTimerAudio() {
  unlockAttempted = true;
  try {
    const ctx = getSharedContext();
    if (!ctx) return false;
    if (ctx.state === 'running') {
      unlocked = true;
      return true;
    }
    if (typeof ctx.resume === 'function') {
      ctx.resume()
        .then(() => { unlocked = ctx.state === 'running'; })
        .catch(() => { /* stays locked; optical alarm still fires */ });
    }
    return unlocked;
  } catch {
    return false;
  }
}

/** @returns {boolean} true once the shared AudioContext is confirmed running. */
export function isTimerAudioUnlocked() {
  return unlocked;
}

/** @returns {boolean} true once an unlock attempt has been made at least once. */
export function hasAttemptedTimerAudioUnlock() {
  return unlockAttempted;
}

function playBeepOnce(ctx) {
  try {
    if (!ctx || ctx.state !== 'running') return;
    const now = ctx.currentTime;
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.type = 'square';
    osc.frequency.value = BEEP_FREQUENCY_HZ;
    gain.gain.setValueAtTime(0, now);
    gain.gain.linearRampToValueAtTime(0.22, now + 0.01);
    gain.gain.linearRampToValueAtTime(0, now + BEEP_DURATION_MS / 1000);
    osc.connect(gain).connect(ctx.destination);
    osc.start(now);
    osc.stop(now + BEEP_DURATION_MS / 1000 + 0.01);
  } catch { /* audio is best-effort; the optical alarm still carries the alert */ }
}

/**
 * Starts a repeating beep pattern (one beep every ~1.2s) until stopped.
 * Feature-detected and best-effort throughout: if Web Audio is unavailable or
 * the context is still locked, this is a silent no-op (the optical alarm
 * carries the alert alone).
 *
 * @returns {() => void} stop function
 */
export function startAlarmTone() {
  let stopped = false;
  let intervalId = null;
  try {
    const ctx = getSharedContext();
    if (ctx && ctx.state === 'suspended' && typeof ctx.resume === 'function') {
      ctx.resume()
        .then(() => { unlocked = ctx.state === 'running'; })
        .catch(() => {});
    }
    playBeepOnce(ctx);
    intervalId = setInterval(() => {
      if (!stopped) playBeepOnce(getSharedContext());
    }, BEEP_INTERVAL_MS);
    if (intervalId && typeof intervalId.unref === 'function') intervalId.unref();
  } catch { /* audio is best-effort */ }

  return function stop() {
    stopped = true;
    if (intervalId !== null) clearInterval(intervalId);
  };
}

function getAudioClass() {
  return typeof Audio !== 'undefined' ? Audio : null;
}

/**
 * Plays the timer's selected chime file on a loop (design Decision G),
 * falling back to the synthesised `startAlarmTone()` beep if Web Audio's
 * `<audio>` element is unavailable, blocked by autoplay policy, or the file
 * fails to load. Never throws. `chime` is resolved through `resolveChimeId()`
 * before being interpolated into the request path — an unknown/invalid value
 * degrades to `classic` rather than reaching the network layer.
 *
 * @param {string|null|undefined} chime
 * @returns {() => void} stop function
 */
export function startChimeAlarm(chime) {
  let stopped = false;
  let audio = null;
  let stopBeep = null;

  function fallbackToBeep() {
    if (stopped || stopBeep) return;
    if (audio) {
      try { audio.pause(); } catch { /* best-effort */ }
      audio = null;
    }
    stopBeep = startAlarmTone();
  }

  try {
    const AudioClass = getAudioClass();
    if (!AudioClass) {
      stopBeep = startAlarmTone();
    } else {
      const resolvedId = resolveChimeId(chime);
      audio = new AudioClass(`/chimes/${resolvedId}.ogg`);
      audio.loop = true;
      audio.addEventListener('error', fallbackToBeep);
      const playResult = audio.play();
      if (playResult && typeof playResult.catch === 'function') {
        playResult.catch(fallbackToBeep);
      }
    }
  } catch {
    fallbackToBeep();
  }

  return function stop() {
    stopped = true;
    if (audio) {
      try {
        audio.pause();
        audio.removeEventListener('error', fallbackToBeep);
        audio.currentTime = 0;
      } catch { /* best-effort */ }
      audio = null;
    }
    if (stopBeep) {
      stopBeep();
      stopBeep = null;
    }
  };
}

// ---------------------------------------------------------------------------
// Optical alarm banner — full-width, flashing (steady under
// prefers-reduced-motion per reducedMotion()), dismissible. Perceivable via
// role="alert" + aria-live="assertive" (same status-banner a11y pattern as
// components/toast.js's role="status", escalated to "alert" because this is
// an active alarm, not a passive notice).
// ---------------------------------------------------------------------------

let soundHintShown = false;

/**
 * Renders the fired-alarm banner into `alarmSurfaceEl` and starts the
 * repeating tone. Dismissing stops the tone, removes the banner, and invokes
 * `onDismiss` — the elapse→cancelled PATCH/DELETE write itself is the 4.4 seam
 * and is left to the caller.
 *
 * @param {{id: string, label: string|null, symbol?: string|null, chime?: string|null}} timer
 * @param {HTMLElement} alarmSurfaceEl
 * @param {() => void} onDismiss
 * @returns {{el: HTMLElement, stop: () => void}}
 */
export function timerAlarmBanner(timer, alarmSurfaceEl, onDismiss) {
  const banner = document.createElement('div');
  banner.className = [
    'card w-full bg-danger/10 border border-danger/40',
    'flex items-center justify-between gap-3',
    reducedMotion() ? '' : 'anim-alarm-flash',
  ].filter(Boolean).join(' ');
  banner.dataset.role = 'timer-alarm-banner';
  banner.dataset.timerId = timer.id;
  banner.setAttribute('role', 'alert');
  banner.setAttribute('aria-live', 'assertive');

  const textWrap = document.createElement('div');
  textWrap.className = 'space-y-1';

  const msg = document.createElement('span');
  msg.className = 'font-semibold text-base block';
  const label = timer.label || t('timers.unnamed_label');
  // textContent only — `symbol`/`label` are user-supplied and must never be
  // parsed as markup.
  msg.textContent = t('timers.alarm_message', { label: timer.symbol ? `${timer.symbol} ${label}` : label });
  textWrap.append(msg);

  const stopTone = startChimeAlarm(timer.chime);

  let hintEl = null;
  if (!isTimerAudioUnlocked() && !soundHintShown) {
    soundHintShown = true;
    hintEl = document.createElement('span');
    hintEl.className = 'text-xs text-ink-muted block';
    hintEl.dataset.role = 'timer-alarm-sound-hint';
    hintEl.textContent = t('timers.enable_sound_hint');
    textWrap.append(hintEl);
  }

  banner.append(textWrap);

  const dismissBtn = document.createElement('button');
  dismissBtn.type = 'button';
  dismissBtn.className = 'btn-primary text-sm shrink-0';
  dismissBtn.dataset.role = 'timer-alarm-dismiss';
  dismissBtn.textContent = t('timers.dismiss');
  dismissBtn.addEventListener('click', () => {
    stopTone();
    banner.remove();
    onDismiss();
  });
  banner.append(dismissBtn);

  alarmSurfaceEl.append(banner);

  return { el: banner, stop: stopTone };
}
